import {
  Box,
  Button,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useContext, useState } from "react";
import { Copy as CopyIcon } from "react-feather";
import { useFragment } from "react-relay";
import {
  AppClipLinkGenerator_brand$data,
  AppClipLinkGenerator_brand$key,
} from "./__generated__/AppClipLinkGenerator_brand.graphql";
import CardSection from "../../../../components/CardSection";
import InfoText from "../../../../components/InfoText";
import SnackbarContext from "../../../../contexts/SnackbarContext";
import { validateUrl } from "../../../../utils/validators";

const brandFragment = graphql`
  fragment AppClipLinkGenerator_brand on BrandType {
    domain
    appConfig {
      id
      iosAppIdentifier
      appViewToUrlMappings {
        appViewType
        regex
        url
      }
    }
  }
`;

const generateAppClipLink = (
  brand: AppClipLinkGenerator_brand$data,
  destinationLink: string
): string => {
  const baseUrl = `https://appclip.apple.com/id?p=${brand.appConfig?.iosAppIdentifier}.PDPClip`;

  const appViewToUrlMappings = brand.appConfig?.appViewToUrlMappings;
  const appViewToUrlMapping = appViewToUrlMappings?.find((mapping) => {
    const regex = new RegExp(mapping.regex);
    return regex.test(destinationLink);
  });

  switch (appViewToUrlMapping?.appViewType) {
    case "PDP":
      const productHandle = destinationLink.split("/").pop()?.split("?")[0];
      return `${baseUrl}&product-handle=${productHandle}`;
    case "COLLECTION":
      const collectionHandle = destinationLink.split("/").pop()?.split("?")[0];
      return `${baseUrl}&collection-handle=${collectionHandle}`;
    default:
      return `${baseUrl}&url=${encodeURIComponent(destinationLink)}`;
  }
};

const AppClipLinkGenerator = ({
  brand: brandKey,
}: {
  brand: AppClipLinkGenerator_brand$key;
}) => {
  const brand = useFragment<AppClipLinkGenerator_brand$key>(
    brandFragment,
    brandKey
  );
  const [destinationLink, setDestinationLink] = useState<string | null>(null);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const snackbar = useContext(SnackbarContext);

  const handleDestinationLinkChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (validateUrl({ url: e.target.value, allowOnlyHttpProtocols: true })) {
      setDestinationLink(e.target.value);
    } else {
      setDestinationLink(null);
    }
  };

  const handleGenerateLink = () => {
    if (!destinationLink) {
      return;
    }

    const appClipLink = generateAppClipLink(brand, destinationLink);
    setGeneratedLink(appClipLink);
  };

  const handleCopyLink = () => {
    if (!generatedLink) {
      return;
    }
    navigator.clipboard.writeText(generatedLink);
    snackbar?.openSnackbar("Link copied to clipboard", "info");
  };

  if (!brand.appConfig?.iosAppIdentifier) {
    return (
      <InfoText variant="warning">
        Your iOS App hasn't been configured yet.
      </InfoText>
    );
  }

  return (
    <CardSection
      title={"App Clip Link Generator"}
      content={
        <Stack spacing={4} width="100%">
          <Stack spacing={2} width="100%">
            <Typography variant="body1">
              Enter the target URL to get started.
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                type="url"
                fullWidth
                autoComplete="off"
                margin="none"
                label="Destination Link"
                variant="outlined"
                onChange={handleDestinationLinkChange}
                placeholder={`${brand.domain}/products/123`}
              />
              <Button
                sx={{
                  flexShrink: 0,
                  flexGrow: 0,
                }}
                disabled={!destinationLink}
                variant="outlined"
                onClick={handleGenerateLink}
              >
                Generate Link
              </Button>
            </Stack>
          </Stack>
          {generatedLink && (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              p="4px 16px"
              borderRadius={1}
              border="1px solid #DBDCDD"
              bgcolor="grey.50"
            >
              <Typography variant="body1">Generated Link:</Typography>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Link
                  href={generatedLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body1"
                  color="secondary"
                  underline="none"
                  sx={{
                    wordBreak: "break-word",
                  }}
                >
                  {generatedLink}
                </Link>
              </Box>
              <IconButton onClick={handleCopyLink}>
                <CopyIcon />
              </IconButton>
            </Stack>
          )}
        </Stack>
      }
    />
  );
};

export default AppClipLinkGenerator;
